import { useState } from "react";
import { useScrolled } from "../../hooks/useScrolled";
import { NAV_LINKS }   from "../../constants/data";
import { colors, fonts } from "../../constants/theme";
import logoMark from "../../assets/logo-mark.png";

export default function Navbar({ currentPage = "home", onNavigate }) {
  const scrolled = useScrolled();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLinkClick = (e, href, label, page = "home", section = null) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (page !== "home") {
      onNavigate?.(page);
      return;
    }

    const scrollToSection = () => {
      if (section) document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    };
    if (currentPage !== "home") {
      onNavigate?.("home");
      if (section) setTimeout(scrollToSection, 100);
    } else {
      scrollToSection();
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate("home");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav
      className={`main-site-nav ${scrolled ? "is-scrolled" : ""}`}
      style={{
        position:       "fixed",
        top:            scrolled ? 10 : 20,
        left:           "50%",
        transform:      "translateX(-50%)",
        width:          "min(1280px, 94%)",
        zIndex:         1000,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        padding:        "0.75rem 1.4rem",
        background:     scrolled ? "rgba(7, 18, 12, 0.94)" : "rgba(10, 24, 15, 0.85)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderRadius:   mobileMenuOpen ? 24 : 9999,
        border:         `1px solid ${scrolled ? "rgba(200, 241, 53, 0.3)" : "rgba(255, 255, 255, 0.12)"}`,
        boxShadow:      scrolled ? "0 16px 36px rgba(0,0,0,0.6), 0 0 20px rgba(200,241,53,0.12)" : "0 10px 30px rgba(0,0,0,0.4)",
        transition:     "all .35s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      {/* Brand / Logo */}
      <a
        href="/"
        onClick={handleLogoClick}
        style={{ display: "flex", alignItems: "center", gap: ".75rem", cursor: "pointer" }}
      >
        <img src={logoMark} alt="" style={{ width: 38, height: 38, objectFit: "contain", filter: `drop-shadow(0 0 10px ${colors.lime}55)`, flexShrink: 0 }} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{
              fontFamily: fonts.display,
              fontWeight: 800,
              fontSize:   "1.2rem",
              color:      "#ffffff",
              letterSpacing: "-0.02em",
              lineHeight: 1.1
            }}
          >
            VittaLink
          </span>
          <span style={{ fontSize: "8.5px", fontWeight: 800, color: colors.lime, letterSpacing: ".14em", marginTop: 1 }}>
            SMART VENDING
          </span>
        </div>
      </a>

      {/* Desktop Nav Links */}
      <ul className="desktop-nav" style={{ display: "flex", gap: "2.2rem", listStyle: "none", alignItems: "center" }}>
        {NAV_LINKS.map(({ label, href, page, section }) => {
          const isActive = page === "home" ? (!section && currentPage === "home") : currentPage === page;
          return (
            <li key={label}>
              <a
                href={href}
                onClick={(e) => handleLinkClick(e, href, label, page, section)}
                style={{
                  fontFamily: fonts.body,
                  fontSize:   ".9rem",
                  fontWeight: isActive ? 800 : 600,
                  color:      isActive ? colors.lime : "#e2e8f0",
                  transition: "all .2s ease",
                  padding:    ".4rem .6rem",
                  borderBottom: isActive ? `2px solid ${colors.lime}` : "none",
                  cursor: "pointer"
                }}
                onMouseEnter={e => {
                  e.target.style.color = colors.lime;
                }}
                onMouseLeave={e => {
                  e.target.style.color = isActive ? colors.lime : "#e2e8f0";
                }}
              >
                {label}
              </a>
            </li>
          );
        })}
      </ul>

      {/* Desktop CTA Button */}
      <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: ".8rem" }}>
        <a
          href="/"
          onClick={(e) => handleLinkClick(e, "/", "App", "home", "app")}
          style={{
            fontFamily:   fonts.body,
            fontSize:     ".88rem",
            fontWeight:   800,
            color:        "#050e08",
            background:   colors.lime,
            padding:      ".6rem 1.4rem",
            borderRadius: 9999,
            boxShadow:    `0 0 20px ${colors.lime}66`,
            transition:   "all .25s ease",
            display:      "inline-flex",
            alignItems:   "center",
            gap:          ".4rem",
            cursor: "pointer"
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = "scale(1.04)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          Obtener App →
        </a>
      </div>

      {/* Mobile Hamburger Button */}
      <button
        className="mobile-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        style={{
          background: "transparent",
          border: "none",
          color: "#fff",
          fontSize: "1.5rem",
          cursor: "pointer",
          padding: ".4rem",
          display: "none"
        }}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? "✕" : "☰"}
      </button>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: ".5rem",
            background: "rgba(9, 20, 13, 0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(200, 241, 53, 0.25)",
            borderRadius: 20,
            padding: "1.2rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            boxShadow: "0 20px 40px rgba(0,0,0,0.6)"
          }}
        >
        {NAV_LINKS.map(({ label, href, page, section }) => (
            <a
              key={label}
              href={href}
              onClick={(e) => handleLinkClick(e, href, label, page, section)}
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: (currentPage === page && (!section || currentPage === "home")) ? colors.lime : "#e2e8f0",
                padding: ".4rem 0"
              }}
            >
              {label}
            </a>
          ))}
          <a
            href="/"
            onClick={(e) => handleLinkClick(e, "/", "App", "home", "app")}
            style={{
              fontFamily: fonts.body,
              fontSize: ".9rem",
              fontWeight: 800,
              color: "#050e08",
              background: colors.lime,
              padding: ".7rem 1.4rem",
              borderRadius: 9999,
              textAlign: "center",
              marginTop: ".5rem"
            }}
          >
            Obtener App →
          </a>
        </div>
      )}
    </nav>
  );
}
